import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  listPostFlightOpportunities,
  refreshPostFlightOpportunities,
  startPostFlightDoubleDown,
  type PostFlightList,
  type StartPostFlightDoubleDownRequest,
} from "@/features/post-flight/api/post-flight.api";
import {
  classifyPostFlightDoubleDownPostError,
  resolvePostFlightDoubleDownErrorToastMessage,
  shouldSuppressPostFlightDoubleDownErrorToast,
  type PostFlightDoubleDownPostFailureKind,
} from "@/features/post-flight/lib/post-flight-double-down-errors";
import {
  postFlightListFreshForMs,
  postFlightRefreshFailureMessage,
} from "@/features/post-flight/lib/post-flight-refresh";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ApiError, shouldSuppressLocalApiErrorToast } from "@/lib/api-client";
import { POST_FLIGHT_DOUBLE_DOWN_POLL_BUDGET_MS } from "@/lib/query-constants";
import { useAmberMutation } from "@/lib/use-amber-mutation";
import { personaOpportunityHubKeys } from "./persona-opportunity-hub.queries";
import { personaOpportunitySetKeys } from "./persona-opportunity-sets.queries";
import { revealGenerationTask } from "./opportunity-sets.queries";

export const postFlightKeys = {
  all: ["post-flight"] as const,
  opportunities: (tenantId: string) => ["post-flight", "opportunities", tenantId] as const,
} as const;

function tenantKey(tenantId: string | null | undefined): string {
  return tenantId || "__none__";
}

/**
 * One payload for every window. The window is a search param, not part of this key,
 * so changing 14 / 30 / 90 does not fetch again.
 */
export function postFlightOpportunitiesQueryOptions(tenantId: string | null | undefined) {
  return queryOptions({
    queryKey: postFlightKeys.opportunities(tenantKey(tenantId)),
    queryFn: () => {
      if (!tenantId) throw new Error("Post-Flight list needs a tenant");
      return listPostFlightOpportunities(tenantId);
    },
    enabled: Boolean(tenantId),
    staleTime: (query) => postFlightListFreshForMs(query.state.data?.fetchedAt, Date.now()),
    refetchOnWindowFocus: true,
    meta: { skipGlobalError: true },
    retry: false,
  });
}

/**
 * Fetches the list from the post-flight service again. The tenant is the
 * mutation variable, not a hook argument, so a result that lands after the
 * user switches organization still goes to the tenant it was fetched for.
 */
export function useRefreshPostFlightOpportunities() {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  return useMutation({
    mutationFn: (tenantId: string) => refreshPostFlightOpportunities(tenantId),
    // The default "online" mode pauses offline: the button sticks on "Updating…" with no
    // message, then fires a paid upstream read by itself when the connection returns.
    networkMode: "always",
    meta: { skipGlobalError: true },
    onSuccess: async (list, tenantId) => {
      const queryKey = postFlightKeys.opportunities(tenantKey(tenantId));
      // A list fetch that started before the refresh would otherwise finish
      // afterwards and put the older list back.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, list);
    },
    onError: (error, tenantId) => {
      if (shouldSuppressLocalApiErrorToast(error)) return;
      // The user has moved on; a toast about this list's age would describe a page they are not on.
      if (useAuth.getState().activeTenantId !== tenantId) return;
      const shown = queryClient.getQueryData<PostFlightList>(postFlightKeys.opportunities(tenantKey(tenantId)));
      toast.error(postFlightRefreshFailureMessage(t, shown?.fetchedAt, i18n.language, Date.now()));
    },
  });
}

export interface UsePostFlightDoubleDownOptions {
  onAccepted?: (variables: StartPostFlightDoubleDownRequest) => void;
  /** Detached poll reached a terminal status, or the one-hour claim window ended. */
  onPollFinished?: (variables: StartPostFlightDoubleDownRequest) => void;
  onPostFailure?: (args: {
    kind: PostFlightDoubleDownPostFailureKind;
    error: ApiError;
  }) => void;
}

export function usePostFlightDoubleDown(options: UsePostFlightDoubleDownOptions = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useAmberMutation<StartPostFlightDoubleDownRequest>({
    mutationFn: async (body) => {
      const result = await startPostFlightDoubleDown(body);
      revealGenerationTask(queryClient);
      return result;
    },
    detachStatusPoll: true,
    onAccepted: options.onAccepted,
    pollBasePath: "/post-flight",
    pollBudgetMs: POST_FLIGHT_DOUBLE_DOWN_POLL_BUDGET_MS,
    passThroughWorkerFailure: true,
    invalidateKeys: [personaOpportunitySetKeys.all, personaOpportunityHubKeys.all],
    skipErrorToast: shouldSuppressPostFlightDoubleDownErrorToast,
    resolveErrorMessage: (error) => resolvePostFlightDoubleDownErrorToastMessage(error, t),
    onSuccess: (result, variables) => {
      if (result.clientAborted === true) return;
      options.onPollFinished?.(variables);
      toast.success(
        t(
          "postFlight.doubleDown.started",
          "Writing scripts in your Post-Flight set",
        ),
      );
    },
    onMutationError: (error, variables) => {
      if (!(error instanceof ApiError)) {
        // Worker FAILED, or the one-hour claim window ended. A transport
        // ApiError while polling stays in flight so a retry matches the claim.
        options.onPollFinished?.(variables);
        return;
      }
      const kind = classifyPostFlightDoubleDownPostError(error);
      if (kind === "stale-list" || kind === "ad-gone") {
        void queryClient.invalidateQueries({
          queryKey: postFlightKeys.opportunities(tenantKey(variables.tenantId)),
        });
      }
      options.onPostFailure?.({ kind, error });
    },
  });
}
